import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { DEVICE_TYPES, REPAIR_TYPES, brandsForDeviceType, modelsForBrand } from "@/data/tws-brands";
import { useLang } from "@/context/LanguageContext";

const emptyForm = {
  device_type: "", brand: "", model: "",
  repair_type: "", description: "",
  name: "", phone: "", email: "",
};

export default function RepairBookingForm() {
  const { t } = useLang();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const brandOptions = useMemo(() => brandsForDeviceType(form.device_type), [form.device_type]);
  const modelOptions = useMemo(() => modelsForBrand(form.device_type, form.brand), [form.device_type, form.brand]);

  const onDeviceTypeChange = (v) => setForm((f) => ({ ...f, device_type: v, brand: "", model: "" }));
  const onBrandChange = (v) => setForm((f) => ({ ...f, brand: v, model: "" }));
  const onModelChange = (v) => setForm((f) => ({ ...f, model: v }));
  const onRepairTypeChange = (v) => setForm((f) => ({ ...f, repair_type: v }));
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.device_type) { toast.error(t("intake.pickDeviceType")); return; }
    if (!form.brand) { toast.error(t("intake.pickBrand")); return; }
    if (!form.model) { toast.error(t("intake.pickModel")); return; }
    if (!form.repair_type) { toast.error(t("intake.pickRepairType")); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        device_type: form.device_type,
        brand: form.brand.trim(),
        model: form.model.trim(),
        device: `${form.brand.trim()} — ${form.model.trim()}`,
        issue_type: form.repair_type.trim(),
        description: form.description.trim(),
      };
      const { data } = await api.post("/repair-intake", payload);
      setSubmitted(data);
      setForm(emptyForm);
      toast.success(t("intake.submitted"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="repair-intake-success" className="bg-white rounded-3xl border border-zinc-100 p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="mt-5 font-display font-extrabold text-2xl text-zinc-900">{t("intake.success")}</h3>
        <p className="mt-2 text-sm text-zinc-500">
          {t("intake.successCopy1")} <span className="font-mono text-zinc-900">{submitted.id}</span>. {t("intake.successCopy2")}
        </p>
        <Button data-testid="intake-new" onClick={() => setSubmitted(null)} className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-white px-6">
          {t("intake.another")}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="repair-intake-form" className="bg-white rounded-3xl border border-zinc-100 p-8 md:p-10 space-y-5">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{t("intake.badge")}</div>
        <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight text-zinc-900">{t("intake.title")}</h2>
        <p className="mt-2 text-sm text-zinc-500">{t("intake.subtitle")}</p>
      </div>

      {/* Device type */}
      <div>
        <Label className="text-sm font-semibold text-zinc-800">{t("intake.deviceType")}</Label>
        <Select value={form.device_type} onValueChange={onDeviceTypeChange}>
          <SelectTrigger data-testid="ri-device-type" className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11"><SelectValue placeholder={t("intake.deviceTypePlaceholder")} /></SelectTrigger>
          <SelectContent className="bg-white">
            {DEVICE_TYPES.map((dt) => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Brand + Model — cascade from device type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold text-zinc-800">{t("intake.brand")}</Label>
          <Select value={form.brand} onValueChange={onBrandChange} disabled={!form.device_type}>
            <SelectTrigger data-testid="ri-brand" className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11 disabled:opacity-60 disabled:cursor-not-allowed">
              <SelectValue placeholder={form.device_type ? t("intake.brandPlaceholder") : t("intake.brandDisabled")} />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {brandOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          {form.brand === "Other" && <div className="mt-1.5 text-xs text-zinc-500">{t("intake.otherHint")}</div>}
        </div>
        <div>
          <Label className="text-sm font-semibold text-zinc-800">{t("intake.model")}</Label>
          <Select value={form.model} onValueChange={onModelChange} disabled={!form.brand}>
            <SelectTrigger data-testid="ri-model" className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11 disabled:opacity-60 disabled:cursor-not-allowed">
              <SelectValue placeholder={form.brand ? t("intake.modelPlaceholder") : t("intake.modelDisabled")} />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {modelOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          {form.model === "Other" && form.brand !== "Other" && <div className="mt-1.5 text-xs text-zinc-500">{t("intake.otherHint")}</div>}
        </div>
      </div>

      {/* Repair type */}
      <div>
        <Label className="text-sm font-semibold text-zinc-800">{t("intake.repairType")}</Label>
        <Select value={form.repair_type} onValueChange={onRepairTypeChange}>
          <SelectTrigger data-testid="ri-repair-type" className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11">
            <SelectValue placeholder={t("intake.repairTypePlaceholder")} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {REPAIR_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <div>
        <Label className="text-sm font-semibold text-zinc-800">{t("intake.describe")}</Label>
        <Textarea
          data-testid="ri-description"
          name="description"
          value={form.description}
          onChange={onChange}
          required
          minLength={5}
          rows={5}
          placeholder={t("intake.describePlaceholder")}
          className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200"
        />
      </div>

      {/* Contact */}
      <div className="pt-2 border-t border-zinc-100">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t("intake.contact")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-semibold text-zinc-800">{t("intake.fullName")}</Label>
            <Input data-testid="ri-name" name="name" value={form.name} onChange={onChange} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-800">{t("intake.phone")}</Label>
            <Input data-testid="ri-phone" name="phone" value={form.phone} onChange={onChange} required minLength={6} placeholder={t("intake.phonePlaceholder")} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-sm font-semibold text-zinc-800">{t("intake.emailAddress")} <span className="text-zinc-400 font-normal">({t("intake.optional")})</span></Label>
          <Input data-testid="ri-email" name="email" type="email" value={form.email} onChange={onChange} placeholder={t("intake.emailPlaceholder")} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
          <div className="mt-1.5 text-xs text-zinc-400">{t("intake.emailHint")}</div>
        </div>
      </div>

      <Button data-testid="ri-submit" type="submit" disabled={submitting} className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12 text-base font-semibold">
        {submitting ? t("intake.submitting") : t("intake.submit")}
      </Button>
      <div className="text-xs text-zinc-400 text-center">
        {t("intake.disclaimer")}
      </div>
    </form>
  );
}

